import type { SgWorkspace } from "./api/types.ts";
import { MixpanelPropertiesProvider } from "@workleap/telemetry/react";
import { type PropsWithChildren, createContext, useContext, useEffect, useMemo } from "react";
import { MixpanelProperty } from "./tracking/mixpanelProperty.ts";
import { useUserIdentification } from "./useUserIdentification.ts";

export type { SgWorkspace };

export interface WorkspaceContextType {
    changeWorkspace: (workspace: SgWorkspace) => void;
    refreshWorkspaces: () => Promise<SgWorkspace[]>;
    workspaces: SgWorkspace[];
    currentWorkspace: SgWorkspace | null;
}

export const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

export const useWorkspaceContext = () => {
    const context = useContext(WorkspaceContext);

    if (!context) {
        throw new Error("useWorkspaceContext must be used within a WorkspaceProvider");
    }

    return context;
};

export function useWorkspace(): SgWorkspace {
    const { currentWorkspace } = useWorkspaceContext();

    if (!currentWorkspace) {
        throw new Error("useWorkspace must be used within a WorkspaceProvider");
    }

    return currentWorkspace;
}

export function useWorkspaceId(): string {
    return useWorkspace().id;
}

interface WorkspaceProviderProps extends PropsWithChildren {
    value: WorkspaceContextType | null;
}

export function WorkspaceProvider({ children, value }: WorkspaceProviderProps) {
    const { identifyWorkspace } = useUserIdentification();
    const workspaceId = value?.currentWorkspace?.id;

    useEffect(() => {
        if (workspaceId) {
            identifyWorkspace(workspaceId);
        }
    }, [workspaceId, identifyWorkspace]);

    const workspaceTelemetryContext = useMemo(() => {
        return { [MixpanelProperty.WorkspaceId]: workspaceId };
    }, [workspaceId]);

    return (
        <WorkspaceContext.Provider value={value}>
            <MixpanelPropertiesProvider value={workspaceTelemetryContext}>{children}</MixpanelPropertiesProvider>
        </WorkspaceContext.Provider>
    );
}
